// src/features/tickets/hooks/useCustomerDeadlines.ts
//
// Rein lesender Hook: liefert die Fristen des Kunden eines Tickets fuer dessen
// aktuellen Arbeitsmonat, inklusive Status (erledigt / wartet auf Kunden /
// zurueckgestellt / ueberfaellig / heute faellig / kommend).
//
// Verwendet exakt dieselben Bausteine wie das Fristen-Dashboard
// (getCurrentWorkingMonth, calculateEffectiveLohnlaufDate, calculateLohnsteuerDeadline,
// customerIndividualDeadlineService, isDeadlineSnoozed) — es wird nichts
// dupliziert, nur fuer einen einzelnen Kunden statt fuer alle berechnet.
// Die Berechnung spiegelt useDeadlines.loadData.

import { useState, useEffect } from 'react';
import { supabase } from '../../../shared/services/supabase';
import {
  Customer,
  CustomerMonthlyStatus,
  CustomerDeadlineView,
  MonthlyDeadline
} from '../../../shared/types';
import {
  calculateEffectiveLohnlaufDate,
  calculateLohnscheinVersandDate,
  calculateLohnsteuerDeadline,
  getCurrentWorkingMonth,
  getLohnscheinPeriode
} from '../../../shared/utils/dateUtils';
import { isDeadlineSnoozed, getIndividualDeadlineKey, hasLohnscheinVersand, ermittleRuhendeFristen } from '../../../shared/utils/deadlineUtils';
import { customerIndividualDeadlineService } from '../../../services/customer/CustomerIndividualDeadlineService';

export type DeadlineState = 'completed' | 'wartet_auf_kunden' | 'snoozed' | 'overdue' | 'today' | 'upcoming';

export interface CustomerDeadlineItem {
  key: string;
  label: string;
  date: Date;
  state: DeadlineState;
}

export interface UseCustomerDeadlinesResult {
  loading: boolean;
  workingMonth: { year: number; month: number } | null;
  items: CustomerDeadlineItem[];
  openCount: number;
  overdueCount: number;
}

// Tagesgenaue Einordnung — gleiche Buckets wie calculateCustomerPriority
// (ueberfaellig vor heute, heute faellig, sonst kommend). Erledigte und
// zurueckgestellte Fristen werden vorrangig markiert.
// WARTET AUF KUNDEN: Ruhende Fristen sind nie überfällig und werden direkt
// nach den erledigten eingeordnet (gleiche Regel wie im Fristenboard).
function classifyState(date: Date, completed: boolean, snoozed: boolean, ruhend: boolean = false): DeadlineState {
  if (completed) return 'completed';
  if (ruhend) return 'wartet_auf_kunden';
  if (snoozed) return 'snoozed';
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const eff = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const daysDiff = Math.floor((eff.getTime() - today.getTime()) / 86400000);
  if (daysDiff < 0) return 'overdue';
  if (daysDiff === 0) return 'today';
  return 'upcoming';
}

export const useCustomerDeadlines = (customer: Customer | null): UseCustomerDeadlinesResult => {
  const [loading, setLoading] = useState(false);
  const [workingMonth, setWorkingMonth] = useState<{ year: number; month: number } | null>(null);
  const [items, setItems] = useState<CustomerDeadlineItem[]>([]);

  const customerId = customer?.id ?? null;

  useEffect(() => {
    if (!customer || !customerId) {
      setItems([]);
      setWorkingMonth(null);
      setLoading(false);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const currentYear = new Date().getFullYear();

        const [statusRes, deadlinesRes, individualDeadlines] = await Promise.all([
          supabase
            .from('customer_monthly_status')
            .select('*')
            .eq('customer_id', customerId)
            .limit(100000),
          supabase
            .from('monthly_deadlines')
            .select('*')
            .gte('year', currentYear - 1)
            .order('year', { ascending: true })
            .order('month', { ascending: true }),
          customerIndividualDeadlineService.getCustomerIndividualDeadlines(customerId)
        ]);

        if (cancelled) return;

        if (statusRes.error) {
          console.error('[useCustomerDeadlines] Status-Load-Error:', statusRes.error);
        }
        if (deadlinesRes.error) {
          console.error('[useCustomerDeadlines] Deadlines-Load-Error:', deadlinesRes.error);
        }

        const statuses = (statusRes.data ?? []) as CustomerMonthlyStatus[];
        const deadlinesData = (deadlinesRes.data ?? []) as MonthlyDeadline[];

        const wm = getCurrentWorkingMonth(customer, statuses);
        const status = statuses.find(s => s.year === wm.year && s.month === wm.month) ?? null;

        // Vormonats-Status fuer die Lohnschein-Frist: Eine relative Frist bezieht
        // den effektiven Lohnlauf der Versand-Periode (Vormonat), der einer dort
        // markierten Datenanlieferung folgt. Bei fester Frist bleibt der Wert
        // ungenutzt (Bezug-Zweig wird nicht betreten).
        const lohnscheinBezugPeriode = getLohnscheinPeriode(wm.year, wm.month);
        const lohnscheinBezugStatus = statuses.find(
          s => s.year === lohnscheinBezugPeriode.year && s.month === lohnscheinBezugPeriode.month
        ) ?? null;

        // Standard- und individuelle Fristen exakt wie im Dashboard berechnen.
        // Lohnlauf 1 + 2 via SSoT (calculateEffectiveLohnlaufDate) — der Status
        // liegt hier bereits vor und liefert eine ggf. markierte Datenanlieferung,
        // sodass die Ticket-Sidebar dasselbe Fristdatum zeigt wie das Fristenboard.
        const deadlines: CustomerDeadlineView['deadlines'] = {};

        if (customer.erster_lohnlauf_tag) {
          deadlines.lohnlauf_1 = calculateEffectiveLohnlaufDate(
            customer,
            1,
            wm.year,
            wm.month,
            status
          );
        }
        if (customer.zweiter_lohnlauf_tag) {
          deadlines.lohnlauf_2 = calculateEffectiveLohnlaufDate(
            customer,
            2,
            wm.year,
            wm.month,
            status
          );
        }
        // Lohnschein-Versand an Arbeitnehmer (Stufe 3.2): fester Tag oder Bezug
        if (hasLohnscheinVersand(customer)) {
          deadlines.lohnschein_versand = calculateLohnscheinVersandDate(customer, wm.year, wm.month, lohnscheinBezugStatus);
        }
        const lohnsteuerDeadline = calculateLohnsteuerDeadline(
          wm.year,
          wm.month,
          customer.lohnsteuer_zeitraum,
          deadlinesData
        );
        if (lohnsteuerDeadline) deadlines.lohnsteuer = lohnsteuerDeadline;

        const md = deadlinesData.find(d => d.year === wm.year && d.month === wm.month);
        if (md?.beitragsnachweis_frist) {
          deadlines.sv_beitraege = new Date(md.beitragsnachweis_frist);
        }

        individualDeadlines.forEach(ind => {
          deadlines[getIndividualDeadlineKey(ind.id)] =
            customerIndividualDeadlineService.calculateIndividualDeadlineDate(wm.year, wm.month, ind, customer, status);
        });

        // In eine Anzeige-Liste mit Status uebersetzen.
        const list: CustomerDeadlineItem[] = [];

        // WARTET AUF KUNDEN: Fristen, die gerade beim Kunden ruhen
        const ruhendeFristen = ermittleRuhendeFristen(customer, status, individualDeadlines);

        const pushStandard = (
          key: string,
          label: string,
          date: Date | undefined,
          completedField: keyof CustomerMonthlyStatus,
          snoozeField: string
        ) => {
          if (!date) return;
          const completed = !!(status && status[completedField]);
          const snoozed = isDeadlineSnoozed(snoozeField, status);
          list.push({ key, label, date, state: classifyState(date, completed, snoozed, ruhendeFristen.has(snoozeField)) });
        };

        pushStandard('lohnlauf_1', 'Lohnlauf 1', deadlines.lohnlauf_1, 'lohnlauf_1_completed', 'lohnlauf_1_completed');
        pushStandard('lohnlauf_2', 'Lohnlauf 2', deadlines.lohnlauf_2, 'lohnlauf_2_completed', 'lohnlauf_2_completed');
        pushStandard('lohnsteuer', 'Lohnsteuer', deadlines.lohnsteuer, 'lohnsteuer_completed', 'lohnsteuer_completed');
        if (customer.sv_pflicht) {
          pushStandard('sv_beitraege', 'SV-Beiträge', deadlines.sv_beitraege, 'sv_beitraege_completed', 'sv_beitraege_completed');
        }
        if (hasLohnscheinVersand(customer)) {
          const lohnscheinPeriode = getLohnscheinPeriode(wm.year, wm.month);
          pushStandard(
            'lohnschein_versand',
            `Lohnschein-Versand (für ${String(lohnscheinPeriode.month).padStart(2, '0')}/${lohnscheinPeriode.year})`,
            deadlines.lohnschein_versand,
            'lohnschein_versand_completed',
            'lohnschein_versand_completed'
          );
        }

        individualDeadlines.forEach(ind => {
          const date = deadlines[getIndividualDeadlineKey(ind.id)];
          if (!date) return; // erledigte einmalige Fristen liefern bewusst kein Datum
          const completed = ind.is_recurring
            ? !!(status && status.individual_deadlines_status && status.individual_deadlines_status[ind.id])
            : !!ind.completed;
          const snoozed = isDeadlineSnoozed(`individual_deadline_${ind.id}`, status);
          list.push({
            key: getIndividualDeadlineKey(ind.id),
            label: ind.title,
            date,
            state: classifyState(date, completed, snoozed, ruhendeFristen.has(`individual_deadline_${ind.id}`))
          });
        });

        setWorkingMonth(wm);
        setItems(list);
      } catch (error) {
        console.error('[useCustomerDeadlines] Load error:', error);
        if (!cancelled) {
          setItems([]);
          setWorkingMonth(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [customer, customerId]);

  const overdueCount = items.filter(i => i.state === 'overdue').length;
  const openCount = items.filter(
    i => i.state === 'overdue' || i.state === 'today' || i.state === 'upcoming'
  ).length;

  return { loading, workingMonth, items, openCount, overdueCount };
};
